
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

interface Article {
  id: string
  slug: string
  category: string
  title: string
  banner_image: string
  content_intro?: string
  content1?: string
  content2?: string
  content3?: string
  content4?: string
  publish_date: string
  location: string
  desc?: string
  created_at: string
  updated_at: string
  image1?: string
  image2?: string
  image3?: string
  image4?: string
  image5?: string
  image6?: string
  image7?: string
  image8?: string
}

interface ContactSubmission {
  id: number
  salutation?: string
  firstName: string
  lastName: string
  email: string
  phoneCountry?: string
  phoneNumber?: string
  countryResidence: string
  issueCategory: string
  message: string
  subscribe: number
  terms: number
  created_at: string
}

interface MembershipApplication {
  id: number
  firstName: string
  lastName: string
  email: string
  phone: string
  dateOfBirth: string
  nationality?: string
  address: string
  city: string
  state?: string
  zipCode?: string
  country: string
  occupation?: string
  company?: string
  annualIncome?: string
  membershipTier: string
  preferredBrands: string
  stylePreferences: string
  sizeInformation: string
  lifestyleEvents: string
  travelFrequency?: string
  shoppingBudget?: string
  referenceSource?: string
  referralName?: string
  termsAccepted: number
  privacyAccepted: number
  marketingConsent: number
  created_at: string
}

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'articles' | 'general' | 'membership' | 'styling' | 'shopping' | 'services' | 'editorial' | 'technical' | 'feedback' | 'partnership' | 'other' | 'editorial-signups'>('articles')
  const [articles, setArticles] = useState<Article[]>([])
  const [contactSubmissions, setContactSubmissions] = useState<ContactSubmission[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingArticle, setEditingArticle] = useState<Article | null>(null)
  const [selectedSubmission, setSelectedSubmission] = useState<ContactSubmission | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [user, setUser] = useState<any>(null)
  const navigate = useNavigate()

  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('fga_admin_token')
    const userData = localStorage.getItem('fga_admin_user')

    if (!token || !userData) {
      window.location.href = '/admin/login'
      return
    }

    setUser(JSON.parse(userData))
    fetchData()
  }, [navigate, activeTab])

  const fetchData = async () => {
    setIsLoading(true)
    setError('')

    try {
      if (activeTab === 'articles') {
        await fetchArticles()
      } else {
        await fetchContactSubmissions()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchArticles = async () => {
    try {
      const response = await fetch('/api/v1/articles')
      if (!response.ok) {
        throw new Error('Failed to fetch articles')
      }
      const data = await response.json()
      setArticles(data)
    } catch (err) {
      throw err
    }
  }

  const fetchContactSubmissions = async () => {
    try {
      const token = localStorage.getItem('fga_admin_token')
      const response = await fetch('/api/v1/contact-submissions', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      if (!response.ok) {
        throw new Error('Failed to fetch contact submissions')
      }
      const data = await response.json()
      setContactSubmissions(data)
    } catch (err) {
      throw err
    }
  }


  const handleLogout = () => {
    localStorage.removeItem('fga_admin_token')
    localStorage.removeItem('fga_admin_user')
    window.location.href = '/admin/login'
  }

  const handleDeleteArticle = async (id: string) => {
    if (!confirm('Are you sure you want to delete this article?')) {
      return
    }

    const token = localStorage.getItem('fga_admin_token')
    if (!token) {
      setError('Authentication required')
      return
    }

    try {
      const response = await fetch(`/api/v1/articles/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to delete article')
      }

      // Refresh articles list
      fetchArticles()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete article')
    }
  }

  const handleViewSubmission = (submission: ContactSubmission) => {
    setSelectedSubmission(submission)
    setShowModal(true)
  }

  const handleDeleteContactSubmission = async (id: number) => {
    if (!confirm('Are you sure you want to delete this contact submission?')) {
      return
    }

    const token = localStorage.getItem('fga_admin_token')
    if (!token) {
      setError('Authentication required')
      return
    }

    try {
      const response = await fetch(`/api/v1/contact-submissions/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to delete contact submission')
      }

      // Refresh contact submissions list
      fetchContactSubmissions()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete contact submission')
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setSelectedSubmission(null)
  }

  const handleEditArticle = (article: Article) => {
    setEditingArticle(article)
    setShowCreateForm(true)
  }

  const handleCreateNew = () => {
    setEditingArticle(null)
    setShowCreateForm(true)
  }

  const handleFormClose = () => {
    setShowCreateForm(false)
    setEditingArticle(null)
    fetchData() // Refresh data after create/update
  }

  if (showCreateForm) {
    return (
      <ArticleForm
        article={editingArticle}
        onClose={handleFormClose}
      />
    )
  }

  return (
    <div className="min-h-screen bg-luxury-black-950">
      {/* Header */}
      <header className="bg-luxury-black-900 border-b border-luxury-gold-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-luxury-gold-400">
                FGA Prestige Admin
              </h1>
              <p className="text-sm text-luxury-cream-200">
                Welcome back, {user?.name}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleCreateNew}
                className="bg-luxury-gold-500 hover:bg-luxury-gold-400 text-luxury-black-950 px-4 py-2 rounded font-medium transition-colors duration-300"
              >
                Create Article
              </button>
              <button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded font-medium transition-colors duration-300"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-luxury-gold-500/20">
            <nav className="-mb-px flex space-x-8 overflow-x-auto">
              <button
                onClick={() => setActiveTab('articles')}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'articles'
                    ? 'border-luxury-gold-500 text-luxury-gold-400'
                    : 'border-transparent text-luxury-cream-300 hover:text-luxury-gold-400 hover:border-luxury-gold-500/50'
                }`}
              >
                Articles
              </button>
              <button
                onClick={() => setActiveTab('general')}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'general'
                    ? 'border-luxury-gold-500 text-luxury-gold-400'
                    : 'border-transparent text-luxury-cream-300 hover:text-luxury-gold-400 hover:border-luxury-gold-500/50'
                }`}
              >
                General Inquiry
              </button>
              <button
                onClick={() => setActiveTab('membership')}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'membership'
                    ? 'border-luxury-gold-500 text-luxury-gold-400'
                    : 'border-transparent text-luxury-cream-300 hover:text-luxury-gold-400 hover:border-luxury-gold-500/50'
                }`}
              >
                Membership Info
              </button>
              <button
                onClick={() => setActiveTab('editorial-signups')}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'editorial-signups'
                    ? 'border-luxury-gold-500 text-luxury-gold-400'
                    : 'border-transparent text-luxury-cream-300 hover:text-luxury-gold-400 hover:border-luxury-gold-500/50'
                }`}
              >
                Editorial Signups
              </button>
            </nav>
          </div>
        </div>

        <div className="bg-luxury-black-900 rounded-lg border border-luxury-gold-500/20">
          <div className="px-6 py-4 border-b border-luxury-gold-500/20">
            <h2 className="text-xl font-semibold text-white">
              {activeTab === 'articles' && 'Article Management'}
              {activeTab === 'general' && 'General Inquiries'}
              {activeTab === 'membership' && 'Membership Information'}
              {activeTab === 'editorial-signups' && 'Editorial Signups'}
              {activeTab === 'styling' && 'Personal Styling'}
              {activeTab === 'shopping' && 'Bespoke Shopping'}
              {activeTab === 'services' && 'Member Services'}
              {activeTab === 'editorial' && 'Editorial & Content'}
              {activeTab === 'technical' && 'Technical Support'}
              {activeTab === 'feedback' && 'Feedback & Suggestions'}
              {activeTab === 'partnership' && 'Partnership Opportunities'}
              {activeTab === 'other' && 'Other Inquiries'}
            </h2>
          </div>

          {error && (
            <div className="px-6 py-4 bg-red-900/50 border-b border-red-500/50">
              <p className="text-red-200">{error}</p>
              <button
                onClick={() => setError('')}
                className="mt-2 text-red-300 hover:text-red-200 underline text-sm"
              >
                Dismiss
              </button>
            </div>
          )}

          <div className="p-6">
            {isLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-luxury-gold-500"></div>
                <p className="mt-2 text-luxury-cream-200">
                  Loading {activeTab === 'articles' ? 'articles' : 'contact submissions'}...
                </p>
              </div>
            ) : activeTab === 'articles' ? (
              articles.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-luxury-cream-200 mb-4">No articles found</p>
                  <button
                    onClick={handleCreateNew}
                    className="bg-luxury-gold-500 hover:bg-luxury-gold-400 text-luxury-black-950 px-4 py-2 rounded font-medium transition-colors duration-300"
                  >
                    Create First Article
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-luxury-gold-500/20">
                    <thead className="bg-luxury-black-800">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-luxury-gold-400 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-luxury-gold-400 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-luxury-gold-400 uppercase tracking-wider">
                          Publish Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-luxury-gold-400 uppercase tracking-wider">
                          Location
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-luxury-gold-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-luxury-black-900 divide-y divide-luxury-gold-500/10">
                      {articles.map((article) => (
                        <tr key={article.id} className="hover:bg-luxury-black-800">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-white">{article.title}</div>
                              <div className="text-sm text-luxury-cream-300">Slug: {article.slug}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-luxury-gold-500/20 text-luxury-gold-400">
                              {article.category}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-luxury-cream-200">
                            {new Date(article.publish_date).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-luxury-cream-200">
                            {article.location}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEditArticle(article)}
                                className="text-luxury-gold-400 hover:text-luxury-gold-300"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteArticle(article.id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (() => {
              // Filter submissions by category
              const categoryMap = {
                'general': 'General Inquiry',
                'membership': 'Membership Information',
                'styling': 'Personal Styling',
                'shopping': 'Bespoke Shopping',
                'services': 'Member Services',
                'editorial': 'Editorial & Content',
                'technical': 'Technical Support',
                'feedback': 'Feedback & Suggestions',
                'partnership': 'Partnership Opportunities',
                'other': 'Other',
                'editorial-signups': 'Editorial Signup'
              };

              const filteredSubmissions = contactSubmissions.filter(
                submission => submission.issueCategory === categoryMap[activeTab as keyof typeof categoryMap]
              );

              return filteredSubmissions.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-luxury-cream-200">No submissions found for this category</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-luxury-gold-500/20">
                    <thead className="bg-luxury-black-800">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-luxury-gold-400 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-luxury-gold-400 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-luxury-gold-400 uppercase tracking-wider">
                          Submitted
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-luxury-gold-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-luxury-black-900 divide-y divide-luxury-gold-500/10">
                      {filteredSubmissions.map((submission) => (
                        <tr key={submission.id} className="hover:bg-luxury-black-800">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-white">
                              {submission.salutation} {submission.firstName} {submission.lastName}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-luxury-cream-200">
                            {submission.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-luxury-cream-200">
                            {new Date(submission.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleViewSubmission(submission)}
                                className="text-luxury-gold-400 hover:text-luxury-gold-300"
                              >
                                View
                              </button>
                              <button
                                onClick={() => handleDeleteContactSubmission(submission.id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        </div>
      </main>

      {/* Modal for viewing contact submission */}
      {showModal && selectedSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-luxury-black-900 rounded-lg border border-luxury-gold-500/20 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-luxury-gold-500/20">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-white">Contact Submission Details</h3>
                <button
                  onClick={closeModal}
                  className="text-luxury-cream-200 hover:text-white text-2xl"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-luxury-cream-100 mb-1">ID</label>
                  <p className="text-luxury-cream-200">{selectedSubmission.id}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-luxury-cream-100 mb-1">Submitted</label>
                  <p className="text-luxury-cream-200">{new Date(selectedSubmission.created_at).toLocaleString()}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-luxury-cream-100 mb-1">Salutation</label>
                  <p className="text-luxury-cream-200">{selectedSubmission.salutation || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-luxury-cream-100 mb-1">Name</label>
                  <p className="text-luxury-cream-200">{selectedSubmission.firstName} {selectedSubmission.lastName}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-luxury-cream-100 mb-1">Email</label>
                <p className="text-luxury-cream-200">{selectedSubmission.email}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-luxury-cream-100 mb-1">Phone Country</label>
                  <p className="text-luxury-cream-200">{selectedSubmission.phoneCountry || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-luxury-cream-100 mb-1">Phone Number</label>
                  <p className="text-luxury-cream-200">{selectedSubmission.phoneNumber || 'N/A'}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-luxury-cream-100 mb-1">Country of Residence</label>
                  <p className="text-luxury-cream-200">{selectedSubmission.countryResidence}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-luxury-cream-100 mb-1">Issue Category</label>
                  <p className="text-luxury-cream-200">{selectedSubmission.issueCategory}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-luxury-cream-100 mb-1">Message</label>
                <div className="bg-luxury-black-800 border border-luxury-gold-500/20 rounded p-3 mt-1">
                  <p className="text-luxury-cream-200 whitespace-pre-wrap">{selectedSubmission.message}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-luxury-cream-100 mb-1">Subscribe to Editorial</label>
                  <p className="text-luxury-cream-200">{selectedSubmission.subscribe ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-luxury-cream-100 mb-1">Terms Accepted</label>
                  <p className="text-luxury-cream-200">{selectedSubmission.terms ? 'Yes' : 'No'}</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-luxury-gold-500/20 flex justify-end">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-luxury-gold-500 hover:bg-luxury-gold-400 text-luxury-black-950 rounded font-medium transition-colors duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Article Form Component
interface ArticleFormProps {
  article?: Article | null
  onClose: () => void
}

const ArticleForm: React.FC<ArticleFormProps> = ({ article, onClose }) => {
  const [formData, setFormData] = useState({
    id: article?.id || '',
    slug: article?.slug || '',
    category: article?.category || '',
    title: article?.title || '',
    banner_image: article?.banner_image || '',
    content_intro: article?.content_intro || '',
    content1: article?.content1 || '',
    content2: article?.content2 || '',
    content3: article?.content3 || '',
    content4: article?.content4 || '',
    publish_date: article?.publish_date || '',
    location: article?.location || '',
    desc: article?.desc || '',
    image1: article?.image1 || '',
    image2: article?.image2 || '',
    image3: article?.image3 || '',
    image4: article?.image4 || '',
    image5: article?.image5 || '',
    image6: article?.image6 || '',
    image7: article?.image7 || '',
    image8: article?.image8 || ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [uploadingImages, setUploadingImages] = useState<{[key: string]: boolean}>({})

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    setError('')
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent, imageKey: string) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImageUpload(e.dataTransfer.files[0], imageKey)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, imageKey: string) => {
    if (e.target.files && e.target.files[0]) {
      handleImageUpload(e.target.files[0], imageKey)
    }
  }

  const handleImageUpload = async (file: File, imageKey: string) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB')
      return
    }

    const token = localStorage.getItem('fga_admin_token')
    if (!token) {
      setError('Authentication required')
      return
    }

    setUploadingImages(prev => ({ ...prev, [imageKey]: true }))
    setError('')

    try {
      const formDataObj = new FormData()
      formDataObj.append('image', file)

      const response = await fetch('/api/v1/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formDataObj,
      })

      if (!response.ok) {
        throw new Error('Failed to upload image')
      }

      const result = await response.json()

      // Update form with the uploaded image URL
      setFormData(prev => ({
        ...prev,
        [imageKey]: result.url
      }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload image')
    } finally {
      setUploadingImages(prev => ({ ...prev, [imageKey]: false }))
    }
  }

  const generateId = () => {
    return Date.now().toString()
  }

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    const token = localStorage.getItem('fga_admin_token')
    if (!token) {
      setError('Authentication required')
      setIsLoading(false)
      return
    }

    try {
      const payload = {
        ...formData,
        id: formData.id || generateId(),
        slug: formData.slug || generateSlug(formData.title)
      }

      const url = article ? `/api/v1/articles/${article.id}` : '/api/v1/articles'
      const method = article ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error(article ? 'Failed to update article' : 'Failed to create article')
      }

      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-luxury-black-950">
      <header className="bg-luxury-black-900 border-b border-luxury-gold-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <h1 className="text-2xl font-bold text-luxury-gold-400">
              {article ? 'Edit Article' : 'Create Article'}
            </h1>
            <button
              onClick={onClose}
              className="text-luxury-cream-200 hover:text-white"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-luxury-black-900 rounded-lg border border-luxury-gold-500/20">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-luxury-cream-100 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-luxury-black-800 border border-luxury-gold-500/30 text-white placeholder-luxury-cream-300 focus:border-luxury-gold-500 focus:ring-1 focus:ring-luxury-gold-500 transition-colors duration-300"
                  placeholder="Article title"
                />
              </div>

              <div>
                <label htmlFor="slug" className="block text-sm font-medium text-luxury-cream-100 mb-2">
                  Slug
                </label>
                <input
                  type="text"
                  id="slug"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-luxury-black-800 border border-luxury-gold-500/30 text-white placeholder-luxury-cream-300 focus:border-luxury-gold-500 focus:ring-1 focus:ring-luxury-gold-500 transition-colors duration-300"
                  placeholder="url-friendly-slug"
                />
              </div>

              <div>
                <label htmlFor="category" className="block text-sm font-medium text-luxury-cream-100 mb-2">
                  Category *
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-luxury-black-800 border border-luxury-gold-500/30 text-white focus:border-luxury-gold-500 focus:ring-1 focus:ring-luxury-gold-500 transition-colors duration-300"
                >
                  <option value="">Select category</option>
                  <option value="Architecture & Design">Architecture & Design</option>
                  <option value="Art & Culture">Art & Culture</option>
                  <option value="Beauty & Wellness">Beauty & Wellness</option>
                  <option value="Business">Business</option>
                  <option value="Food & Drinks">Food & Drinks</option>
                  <option value="Lifestyle">Lifestyle</option>
                  <option value="Restaurants & Nightlife">Restaurants & Nightlife</option>
                  <option value="Style & Fashion">Style & Fashion</option>
                  <option value="Travel">Travel</option>
                  <option value="Weddings">Weddings</option>
                </select>
              </div>

              <div>
                <label htmlFor="location" className="block text-sm font-medium text-luxury-cream-100 mb-2">
                  Location *
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-luxury-black-800 border border-luxury-gold-500/30 text-white placeholder-luxury-cream-300 focus:border-luxury-gold-500 focus:ring-1 focus:ring-luxury-gold-500 transition-colors duration-300"
                  placeholder="City, Country"
                />
              </div>

              <div>
                <label htmlFor="publish_date" className="block text-sm font-medium text-luxury-cream-100 mb-2">
                  Publish Date *
                </label>
                <input
                  type="date"
                  id="publish_date"
                  name="publish_date"
                  value={formData.publish_date}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-luxury-black-800 border border-luxury-gold-500/30 text-white focus:border-luxury-gold-500 focus:ring-1 focus:ring-luxury-gold-500 transition-colors duration-300"
                />
              </div>

              <div>
                <label htmlFor="banner_image" className="block text-sm font-medium text-luxury-cream-100 mb-2">
                  Banner Image *
                </label>
                
                {/* Drag and Drop Area */}
                <div
                  className={`
                    relative border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-300
                    ${dragActive
                      ? 'border-luxury-gold-500 bg-luxury-gold-500/10'
                      : 'border-luxury-gold-500/30 hover:border-luxury-gold-500/50'
                    }
                    ${isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={(e) => handleDrop(e, 'banner_image')}
                  onClick={() => document.getElementById('file-input')?.click()}
                >
                  <input
                    id="file-input"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileSelect(e, 'banner_image')}
                    className="hidden"
                    disabled={isUploading}
                  />
                  
                  {isUploading ? (
                    <div className="flex flex-col items-center">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-luxury-gold-500 mb-2"></div>
                      <p className="text-luxury-cream-200">Uploading image...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <svg className="w-12 h-12 text-luxury-gold-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p className="text-luxury-cream-200 mb-1">
                        Drag and drop image here, or click to select
                      </p>
                      <p className="text-xs text-luxury-cream-300">
                        PNG, JPG, GIF up to 5MB
                      </p>
                    </div>
                  )}
                </div>

                {/* Image Preview */}
                {formData.banner_image && (
                  <div className="mt-4">
                    <p className="text-sm text-luxury-cream-200 mb-2">Preview:</p>
                    <img
                      src={formData.banner_image}
                      alt="Banner preview"
                      className="w-full h-48 object-cover rounded-lg border border-luxury-gold-500/20"
                    />
                  </div>
                )}

                {/* Manual URL Input */}
                <div className="mt-4">
                  <label htmlFor="banner_image_url" className="block text-sm font-medium text-luxury-cream-100 mb-2">
                    Or enter image URL manually:
                  </label>
                  <input
                    type="text"
                    id="banner_image_url"
                    name="banner_image"
                    value={formData.banner_image}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-luxury-black-800 border border-luxury-gold-500/30 text-white placeholder-luxury-cream-300 focus:border-luxury-gold-500 focus:ring-1 focus:ring-luxury-gold-500 transition-colors duration-300"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
              </div>
            </div>

            {/* Additional Images */}
            <div className="mt-8">
              <h3 className="text-lg font-medium text-luxury-gold-400 mb-4">Additional Images</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {['image1', 'image2', 'image3', 'image4', 'image5', 'image6', 'image7', 'image8'].map((imageKey) => (
                  <div key={imageKey} className="space-y-4">
                    <label className="block text-sm font-medium text-luxury-cream-100">
                      {imageKey.charAt(0).toUpperCase() + imageKey.slice(1).replace('image', ' Image ')}
                    </label>

                    {/* Drag and Drop Area */}
                    <div
                      className={`
                        relative border-2 border-dashed rounded-lg p-4 text-center transition-colors duration-300
                        ${dragActive
                          ? 'border-luxury-gold-500 bg-luxury-gold-500/10'
                          : 'border-luxury-gold-500/30 hover:border-luxury-gold-500/50'
                        }
                        ${uploadingImages[imageKey] ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                      `}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={(e) => handleDrop(e, imageKey)}
                      onClick={() => document.getElementById(`${imageKey}-input`)?.click()}
                    >
                      <input
                        id={`${imageKey}-input`}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileSelect(e, imageKey)}
                        className="hidden"
                        disabled={uploadingImages[imageKey]}
                      />

                      {uploadingImages[imageKey] ? (
                        <div className="flex flex-col items-center">
                          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-luxury-gold-500 mb-1"></div>
                          <p className="text-xs text-luxury-cream-200">Uploading...</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center">
                          <svg className="w-8 h-8 text-luxury-gold-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <p className="text-xs text-luxury-cream-200 mb-1">
                            Upload {imageKey.replace('image', 'Image ')}
                          </p>
                          <p className="text-xs text-luxury-cream-300">
                            PNG, JPG, GIF up to 5MB
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Image Preview */}
                    {formData[imageKey as keyof typeof formData] && (
                      <div>
                        <img
                          src={formData[imageKey as keyof typeof formData] as string}
                          alt={`${imageKey} preview`}
                          className="w-full h-24 object-cover rounded border border-luxury-gold-500/20"
                        />
                      </div>
                    )}

                    {/* Manual URL Input */}
                    <input
                      type="text"
                      name={imageKey}
                      value={formData[imageKey as keyof typeof formData] as string}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-luxury-black-800 border border-luxury-gold-500/30 text-white placeholder-luxury-cream-300 focus:border-luxury-gold-500 focus:ring-1 focus:ring-luxury-gold-500 transition-colors duration-300 text-xs"
                      placeholder={`URL for ${imageKey}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="desc" className="block text-sm font-medium text-luxury-cream-100 mb-2">
                Description
              </label>
              <input
                type="text"
                id="desc"
                name="desc"
                value={formData.desc}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-luxury-black-800 border border-luxury-gold-500/30 text-white placeholder-luxury-cream-300 focus:border-luxury-gold-500 focus:ring-1 focus:ring-luxury-gold-500 transition-colors duration-300"
                placeholder="Brief description"
              />
            </div>

            {/* Content Sections */}
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-luxury-gold-400">Content Sections</h3>

              <div>
                <label htmlFor="content_intro" className="block text-sm font-medium text-luxury-cream-100 mb-2">
                  Introduction Content
                </label>
                <textarea
                  id="content_intro"
                  name="content_intro"
                  value={formData.content_intro}
                  onChange={handleInputChange}
                  rows={6}
                  className="w-full px-4 py-3 bg-luxury-black-800 border border-luxury-gold-500/30 text-white placeholder-luxury-cream-300 focus:border-luxury-gold-500 focus:ring-1 focus:ring-luxury-gold-500 transition-colors duration-300"
                  placeholder="Introduction content (HTML supported)"
                />
              </div>

              <div>
                <label htmlFor="content1" className="block text-sm font-medium text-luxury-cream-100 mb-2">
                  Content Section 1 *
                </label>
                <textarea
                  id="content1"
                  name="content1"
                  value={formData.content1}
                  onChange={handleInputChange}
                  required
                  rows={8}
                  className="w-full px-4 py-3 bg-luxury-black-800 border border-luxury-gold-500/30 text-white placeholder-luxury-cream-300 focus:border-luxury-gold-500 focus:ring-1 focus:ring-luxury-gold-500 transition-colors duration-300"
                  placeholder="First content section with H2 heading (HTML supported)"
                />
              </div>

              <div>
                <label htmlFor="content2" className="block text-sm font-medium text-luxury-cream-100 mb-2">
                  Content Section 2
                </label>
                <textarea
                  id="content2"
                  name="content2"
                  value={formData.content2}
                  onChange={handleInputChange}
                  rows={8}
                  className="w-full px-4 py-3 bg-luxury-black-800 border border-luxury-gold-500/30 text-white placeholder-luxury-cream-300 focus:border-luxury-gold-500 focus:ring-1 focus:ring-luxury-gold-500 transition-colors duration-300"
                  placeholder="Second content section with H2 heading (HTML supported)"
                />
              </div>

              <div>
                <label htmlFor="content3" className="block text-sm font-medium text-luxury-cream-100 mb-2">
                  Content Section 3
                </label>
                <textarea
                  id="content3"
                  name="content3"
                  value={formData.content3}
                  onChange={handleInputChange}
                  rows={8}
                  className="w-full px-4 py-3 bg-luxury-black-800 border border-luxury-gold-500/30 text-white placeholder-luxury-cream-300 focus:border-luxury-gold-500 focus:ring-1 focus:ring-luxury-gold-500 transition-colors duration-300"
                  placeholder="Third content section with H2 heading (HTML supported)"
                />
              </div>

              {/* <div>
                <label htmlFor="content4" className="block text-sm font-medium text-luxury-cream-100 mb-2">
                  Content Section 4
                </label>
                <textarea
                  id="content4"
                  name="content4"
                  value={formData.content4}
                  onChange={handleInputChange}
                  rows={8}
                  className="w-full px-4 py-3 bg-luxury-black-800 border border-luxury-gold-500/30 text-white placeholder-luxury-cream-300 focus:border-luxury-gold-500 focus:ring-1 focus:ring-luxury-gold-500 transition-colors duration-300"
                  placeholder="Fourth content section with H2 heading (HTML supported)"
                />
              </div> */}
            </div>

            {error && (
              <div className="bg-red-900/50 border border-red-500/50 text-red-200 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border border-luxury-gold-500/30 text-luxury-cream-200 hover:text-white transition-colors duration-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className={`
                  px-6 py-3 font-medium transition-all duration-300
                  ${isLoading
                    ? 'bg-luxury-gold-500/50 text-luxury-black-700 cursor-not-allowed'
                    : 'bg-luxury-gold-500 hover:bg-luxury-gold-400 text-luxury-black-950'
                  }
                `}
              >
                {isLoading ? 'Saving...' : (article ? 'Update Article' : 'Create Article')}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  )
}

export default AdminDashboard